/**
 * NIS2-Beratung – Inhalte für den Hub (/nis2-beratung) und die drei
 * Anzeigen-Landingpages.
 *
 * Aufbau nach dem NIS2 Go-to-Market-Playbook (Stand 11.09.2026), Abschnitt
 * 3.2: Hero → Vertrauensleiste → Problem → Lösung → Ablauf → Preis → Über
 * mich → FAQ → Abschluss-CTA. Jede Landingpage hat genau ein Ziel: das
 * Erstgespräch.
 *
 * Regeln für diese Datei – sie stehen hier, weil sie beim nächsten Nachtrag
 * am leichtesten vergessen werden:
 *
 *  - Rechtsfakten nur, wenn belegt. Geprüft im September 2026 gegen WKO und
 *    Schönherr: Inkrafttreten 1.10.2026, Registrierung bis 31.12.2026,
 *    Selbstdeklaration bis 30.09.2027, § 31 (Leitungsorgane), § 32
 *    (Risikomanagementmaßnahmen), Größenschwellen. Keine erfundenen
 *    Schulungsfristen, keine Strafdramatik – das ist laut Playbook das
 *    sichtbare Unterscheidungsmerkmal gegenüber Mitbewerbern.
 *  - Kein „NIS2-Zertifikat“ versprechen. Es gibt kein staatliches.
 *  - Preise sind Richtwerte netto aus dem Playbook, als „ab“-Preise. Den
 *    dort genannten Einführungspreis für den Quick-Check nennen wir bewusst
 *    nicht als Streichpreis: Ein durchgestrichener Preis, der nie verlangt
 *    wurde, ist ein klassischer UWG-Abmahngrund.
 *  - Keine Kundenlogos auf diesen Seiten (Playbook: nur mit schriftlicher
 *    Freigabe und nur für tatsächlich passende Leistungen).
 */

import type { IconName } from "@/components/ui/Icon";

export const NIS2_STAND = "2026-09-11";

/** Fristen – nur belegte Termine. */
export const fristen = [
  {
    datum: "1. Oktober 2026",
    iso: "2026-10-01",
    titel: "NISG 2026 tritt in Kraft",
    text: "Ab diesem Tag gelten die Pflichten des Gesetzes für wesentliche und wichtige Einrichtungen.",
  },
  {
    datum: "31. Dezember 2026",
    iso: "2026-12-31",
    titel: "Registrierung",
    text: "Betroffene Einrichtungen registrieren sich binnen drei Monaten nach Inkrafttreten bei der Behörde.",
  },
  {
    datum: "30. September 2027",
    iso: "2027-09-30",
    titel: "Selbstdeklaration",
    text: "Wesentliche und wichtige Einrichtungen übermitteln ihre Selbstdeklaration zur Umsetzung der Maßnahmen.",
  },
] as const;

/**
 * Die zehn Maßnahmenbereiche. § 32 NISG 2026 setzt Art. 21 Abs. 2 der
 * NIS2-Richtlinie um; die Liste folgt dem Richtlinientext.
 */
export const massnahmen = [
  { titel: "Risikoanalyse & Sicherheitskonzepte", text: "Welche Systeme sind kritisch, was bedroht sie, was ist die Leitlinie." },
  { titel: "Bewältigung von Sicherheitsvorfällen", text: "Erkennen, eindämmen, melden – mit festgelegten Rollen." },
  { titel: "Betrieb aufrechterhalten", text: "Backup, Wiederherstellung und Krisenmanagement, getestet statt behauptet." },
  { titel: "Sicherheit der Lieferkette", text: "Anforderungen an Dienstleister und Lieferanten – und deren Nachweis." },
  { titel: "Sichere Beschaffung & Entwicklung", text: "Sicherheit bei Kauf, Entwicklung und Wartung, inklusive Schwachstellen." },
  { titel: "Wirksamkeit bewerten", text: "Regelmäßig prüfen, ob die Maßnahmen tatsächlich greifen." },
  { titel: "Cyberhygiene & Schulungen", text: "Grundregeln im Alltag und Schulung der Mitarbeitenden." },
  { titel: "Kryptografie & Verschlüsselung", text: "Wo Verschlüsselung nötig ist und wie Schlüssel verwaltet werden." },
  { titel: "Personal, Zugriffe & Anlagen", text: "Wer darf was, wer hat welche Geräte, was passiert beim Austritt." },
  { titel: "MFA & gesicherte Kommunikation", text: "Mehr-Faktor-Anmeldung und Kommunikationswege für den Notfall." },
] as const;

export type Paket = {
  stufe: number;
  name: string;
  inhalt: string;
  preis: string;
  href?: string;
};

/** Angebotsleiter aus Playbook 2.5 – Richtwerte netto, zzgl. USt. */
export const pakete: Paket[] = [
  { stufe: 0, name: "Betroffenheitscheck", inhalt: "Online-Check in 3 Minuten, danach auf Wunsch 20 Minuten Gespräch", preis: "kostenlos", href: "/nis2-check" },
  { stufe: 1, name: "NIS2-Quick-Check", inhalt: "Halber Tag remote: Einstufung, die zehn größten Lücken, Empfehlung, Kurzbericht", preis: "990 €" },
  { stufe: 1, name: "Schulung der Geschäftsführung", inhalt: "3 Stunden inhouse oder online, bis 5 Leitungsorgane, Teilnahmebestätigung", preis: "ab 1.190 €", href: "/nis2-schulung-geschaeftsfuehrung" },
  { stufe: 1, name: "Registrierungs-Service", inhalt: "Einstufung dokumentieren, Angaben sammeln, Registrierung begleiten", preis: "ab 490 €" },
  { stufe: 2, name: "Lieferanten-Paket", inhalt: "Kundenfragebogen plus Minimalprogramm in 8 Wochen, Dokumente, Awareness", preis: "ab 2.900 €", href: "/nis2-lieferanten" },
  { stufe: 2, name: "Gap-Analyse & Maßnahmenplan", inhalt: "Für wichtige Einrichtungen: Reifegrad je Maßnahme, Risiken, Roadmap", preis: "ab 4.900 €", href: "/nis2-gap-analyse" },
  { stufe: 2, name: "Tabletop-Übung", inhalt: "2–3 Stunden Planspiel mit Geschäftsführung, IT und Dienstleister, mit Bericht", preis: "ab 1.490 €" },
  { stufe: 3, name: "Umsetzungsbegleitung", inhalt: "Dokumentenset, Notfallhandbuch, Meldeprozess, Lieferantenmanagement", preis: "ab 950 € / Tag" },
  { stufe: 3, name: "Selbstdeklarations-Service", inhalt: "Vorbereitung und Befüllung der Selbstdeklaration bis 30.09.2027", preis: "ab 990 €" },
  { stufe: 4, name: "Jahresbetreuung", inhalt: "Externe Informationssicherheit: Jour fixe, Kennzahlenbericht, Ansprechpartner bei Vorfällen", preis: "ab 290 € / Monat" },
];

/** Vertrauensleiste – nur, was tatsächlich zutrifft. */
export const vertrauen = [
  "Ing. · MSc",
  "CMSE – Maschinensicherheit",
  "Testmanagement in regulierten Projekten",
  "Fixpreis statt Stundenzettel",
  "Österreichisches Recht: NISG 2026",
] as const;

export const berater = {
  kurz:
    "Ingenieur, Testmanager in regulierten Softwareprojekten und Experte für Maschinensicherheit (CMSE). Über 15 Jahre Enterprise-Projekterfahrung, Gastlektor.",
  methode:
    "Aus dem Testmanagement kommt die Methode: Jede Anforderung bekommt eine Maßnahme und einen Nachweis – wie in einer Testmatrix. Was so dokumentiert ist, hält einer Prüfung stand, statt nur gut auszusehen.",
  zusage: "Sie haben einen festen Ansprechpartner, vom Erstgespräch bis zur Übergabe.",
} as const;

export const foerderhinweis =
  "Beratungsleistungen zur Cybersicherheit können je nach Programm und Bundesland gefördert werden – etwa über den Cyber Security Scheck, KMU.DIGITAL oder Landesprogramme. Ob gerade ein Call offen ist, klären wir im Erstgespräch. Wichtig: Den Antrag stellen Sie vor Auftragsbeginn.";

export type Nis2Landing = {
  slug: "nis2-lieferanten" | "nis2-schulung-geschaeftsfuehrung" | "nis2-gap-analyse";
  icon: IconName;
  navTitle: string;
  tagline: string;
  keywords: string[];
  /** Ohne Zusatz – die Vorlage im Layout hängt „| WeDev-IT“ an, daher max. ~49 Zeichen. */
  metaTitle: string;
  metaDescription: string;
  heroEyebrow: string;
  heroHeadline: string;
  heroSub: string;
  problem: { heading: string; punkte: string[] };
  loesung: { heading: string; intro: string; bausteine: { titel: string; text: string }[] };
  ablauf: { heading: string; schritte: { titel: string; zeit: string; text: string }[] };
  preis: { betrag: string; hinweis: string; enthalten: string[]; nichtEnthalten: string[] };
  vertiefung: { heading: string; body: string }[];
  faq: { q: string; a: string }[];
  /** Vorbelegung für das Kontaktformular. */
  anlass: string;
};

export const landings: Nis2Landing[] = [
  {
    slug: "nis2-lieferanten",
    icon: "truck",
    navTitle: "NIS2 für Lieferanten",
    tagline: "Kundenfragebogen prüfbar beantworten, in 4–8 Wochen.",
    keywords: [
      "nis2 lieferant",
      "nis2 lieferanten",
      "nis2 fragebogen lieferant",
      "nis2 lieferkette anforderungen",
      "nis2 nachweis kunde",
      "nis2 zulieferer österreich",
    ],
    metaTitle: "NIS2 für Lieferanten: Fragebogen in 4–8 Wochen",
    metaDescription:
      "Ihr Kunde verlangt NIS2-Nachweise? Wir beantworten den Fragebogen prüfbar, mit den Maßnahmen dahinter. Fixpreis ab 2.900 €, kostenloses Erstgespräch.",
    heroEyebrow: "NIS2 für Zulieferer & Dienstleister",
    heroHeadline: "Ihr Kunde verlangt NIS2-Nachweise? Wir beantworten den Fragebogen – prüfbar, in 4–8 Wochen.",
    heroSub:
      "Für Zulieferer und Dienstleister mit 10–250 Mitarbeitenden in Österreich. Fixpreis, klare Schritte, keine Panikmache.",
    problem: {
      heading: "Die Lage, in der die meisten anfragen",
      punkte: [
        "Der Fragebogen hat 60 Fragen, und bei der Hälfte ist unklar, was genau gemeint ist.",
        "Ihr IT-Dienstleister sagt, Richtlinien und Nachweise seien nicht sein Job.",
        "Die Frist läuft – und der Auftrag hängt daran.",
      ],
    },
    loesung: {
      heading: "Was in den acht Wochen konkret passiert",
      intro:
        "Kein Berg an Papier, sondern genau das, was Ihr Kunde sehen will – und was im Betrieb tatsächlich umgesetzt ist.",
      bausteine: [
        { titel: "Fragebogen-Analyse", text: "Jede Frage wird einer Anforderung zugeordnet: Was ist gemeint, was haben Sie schon, was fehlt." },
        { titel: "Schnelle Verbesserungen", text: "Mehr-Faktor-Anmeldung, geprüftes Backup, getrennte Admin-Konten – die Punkte, die fast jeder Fragebogen zuerst prüft." },
        { titel: "Leitlinie & Kerndokumente", text: "Eine Informationssicherheitsleitlinie und die Dokumente, die Ihr Kunde als Beleg erwartet – auf Ihren Betrieb zugeschnitten." },
        { titel: "Notfall- & Meldeplan", text: "Wer tut was, wenn etwas passiert – und wer informiert wen, bei Ihnen und bei Ihrem Kunden." },
        { titel: "Schulung der Belegschaft", text: "60–90 Minuten Awareness mit Teilnahmeliste – ein Nachweis, den viele Fragebögen ausdrücklich verlangen." },
        { titel: "Beantworteter Fragebogen", text: "Jede Antwort mit Verweis auf den passenden Nachweis. Zum Weiterleiten an Ihren Kunden." },
      ],
    },
    ablauf: {
      heading: "Vier Schritte bis zur Übergabe an Ihren Kunden",
      schritte: [
        { titel: "Erstgespräch", zeit: "20 Minuten", text: "Wir sehen uns den Fragebogen an und klären Frist und Umfang. Danach bekommen Sie einen Fixpreis." },
        { titel: "Analyse", zeit: "Woche 1", text: "Bestandsaufnahme mit Ihnen und Ihrem IT-Dienstleister, Zuordnung jeder Frage zu Maßnahme und Nachweis." },
        { titel: "Umsetzung", zeit: "Woche 2–6", text: "Schnelle Verbesserungen, Dokumente, Notfallplan und Schulung. Wöchentlich 30 Minuten Abstimmung." },
        { titel: "Übergabe", zeit: "Woche 7–8", text: "Fragebogen mit Belegen, Fortschrittsliste und alle Dokumente – bereit für Ihren Kunden." },
      ],
    },
    preis: {
      betrag: "ab 2.900 €",
      hinweis: "netto zzgl. USt · Fixpreis nach dem Erstgespräch, abhängig vom Umfang des Fragebogens",
      enthalten: [
        "Analyse des Fragebogens und Zuordnung zu Maßnahmen",
        "Leitlinie, Kerndokumente, Notfall- und Meldeplan",
        "Awareness-Schulung für bis zu 25 Mitarbeitende",
        "Beantworteter Fragebogen mit Nachweisliste",
        "Wöchentliche Abstimmung, fester Ansprechpartner",
      ],
      nichtEnthalten: [
        "Technische Umsetzung durch Ihren IT-Dienstleister",
        "Rechtsberatung",
        "Zertifizierungsaudit",
      ],
    },
    vertiefung: [
      {
        heading: "Warum Sie betroffen sind, obwohl das Gesetz Sie nicht nennt",
        body:
          "Ein Metallverarbeiter mit 35 Mitarbeitenden fällt meist nicht selbst unter das NISG 2026. Seine Kunden aber schon – und zu den zehn Maßnahmenbereichen, die sie umsetzen müssen, gehört ausdrücklich die Sicherheit der Lieferkette. Also geben sie die Anforderungen weiter: als Fragebogen, als Vertragsnachtrag oder als Audit. Für einen Zulieferer ist deshalb nicht die rechtliche Einstufung die entscheidende Frage, sondern ob er den Fragebogen seines Kunden besteht.",
      },
      {
        heading: "Was in solchen Fragebögen typischerweise steht",
        body:
          "Die Formulierungen unterscheiden sich, die Themen kaum: Mehr-Faktor-Anmeldung, Backup und geprüfte Wiederherstellung, Umgang mit Updates, getrennte Administratorkonten, Ansprechpartner und Ablauf bei Sicherheitsvorfällen, Zugriffsrechte, Schulung der Belegschaft, Fernwartungszugänge von Dienstleistern und Verschlüsselung. Bei vielen Fragen reicht kein Häkchen – verlangt wird ein Beleg. Genau dort scheitern die meisten Antworten: nicht an der Technik, sondern am Nachweis.",
      },
      {
        heading: "Die Methode: Anforderung, Maßnahme, Nachweis",
        body:
          "Jede Frage aus dem Fragebogen landet in einer Fortschrittsliste mit vier Spalten: Anforderung, Maßnahme, Nachweis, Status. Das ist die Testmatrix aus dem Testmanagement, übertragen auf Informationssicherheit. Sie sehen jede Woche, was erledigt ist und was fehlt – und am Ende gibt es zu jeder Antwort einen Beleg, den Sie Ihrem Kunden zeigen können.",
      },
      {
        heading: "Und Ihr IT-Dienstleister?",
        body:
          "Er bleibt, was er ist: der, der Ihre Technik kennt und betreibt. Was die meisten IT-Dienstleister nicht leisten, sind Leitlinien, Notfallpläne und Nachweisdokumentation – das übernehmen wir. In der Praxis arbeiten wir mit ihm zusammen: Er setzt die technischen Punkte um, wir sorgen dafür, dass sie dokumentiert und belegbar sind.",
      },
    ],
    faq: [
      { q: "Sind wir selbst von NIS2 betroffen?", a: "Das hängt von Branche und Größe ab. Viele Zulieferer sind es rechtlich nicht – müssen die Anforderungen aber trotzdem erfüllen, weil ihre Kunden es verlangen. Der kostenlose Betroffenheitscheck gibt in drei Minuten eine erste Einschätzung." },
      { q: "Was, wenn wir keine eigene IT-Abteilung haben?", a: "Das ist bei Betrieben Ihrer Größe der Normalfall. Wir arbeiten mit Ihrem externen IT-Dienstleister zusammen oder, wenn es keinen gibt, mit der Person, die sich bei Ihnen um die IT kümmert." },
      { q: "Arbeiten Sie mit unserem IT-Dienstleister zusammen?", a: "Ja. Er setzt die technischen Maßnahmen um, wir kümmern uns um Leitlinien, Dokumentation und Nachweise. Das spart doppelte Arbeit und vermeidet Reibung." },
      { q: "Wie viel Zeit kostet uns das?", a: "Rechnen Sie mit rund ein bis zwei Stunden pro Woche auf Ihrer Seite – für die wöchentliche Abstimmung und Rückfragen. Den Rest übernehmen wir." },
      { q: "Bekommen wir ein NIS2-Zertifikat?", a: "Nein – ein staatliches NIS2-Zertifikat gibt es nicht, und wer eines verspricht, verkauft etwas, das nicht existiert. Sie bekommen prüfbare Nachweise zu jeder Antwort, und genau die verlangt Ihr Kunde." },
      { q: "Gibt es dafür Förderungen?", a: foerderhinweis },
    ],
    anlass: "Ein Kunde verlangt NIS2-Nachweise bzw. hat einen Fragebogen geschickt.",
  },
  {
    slug: "nis2-schulung-geschaeftsfuehrung",
    icon: "users",
    navTitle: "NIS2-Schulung Geschäftsführung",
    tagline: "§ 31 NISG 2026 – verständlich, 3 Stunden, mit Nachweis.",
    keywords: [
      "nis2 schulung geschäftsführung",
      "nis2 schulung geschäftsführer",
      "nis2 schulung leitungsorgane",
      "cybersicherheitsschulung geschäftsführung",
      "nisg 2026 schulung",
      "§ 31 nisg",
    ],
    metaTitle: "NIS2-Schulung für die Geschäftsführung (§ 31)",
    metaDescription:
      "Die Leitungsorgane müssen an Cybersicherheitsschulungen teilnehmen (§ 31 NISG 2026). 3 Stunden, inhouse oder online, mit Teilnahmebestätigung. Ab 1.190 €.",
    heroEyebrow: "Schulung nach § 31 NISG 2026",
    heroHeadline: "NIS2-Schulung für die Geschäftsführung – verständlich, in 3 Stunden, mit Nachweis.",
    heroSub:
      "Das NISG 2026 verpflichtet die Leitungsorgane betroffener Einrichtungen, an Cybersicherheitsschulungen teilzunehmen. Inhouse oder online, für bis zu fünf Personen, zum Fixpreis.",
    problem: {
      heading: "Warum Geschäftsführer diese Schulung buchen",
      punkte: [
        "Sie sollen die Einhaltung der Sicherheitsmaßnahmen sicherstellen und beaufsichtigen – aber niemand hat erklärt, was das konkret heißt.",
        "Was Sie bisher gelesen haben, war entweder Juristendeutsch oder Panikmache.",
        "Sie brauchen einen Nachweis, dass Sie Ihre Pflicht erfüllt haben.",
      ],
    },
    loesung: {
      heading: "Die drei Stunden im Überblick",
      intro: "Fünf Blöcke, jeder mit einer Frage, die die Geschäftsführung danach beantworten kann.",
      bausteine: [
        { titel: "Bedrohungslage – 30 Min.", text: "Wie Angriffe auf österreichische Betriebe tatsächlich ablaufen und welche Wege Angreifer bevorzugen." },
        { titel: "NISG 2026 – 40 Min.", text: "Betroffenheit, Pflichten, Fristen, Aufsicht, Sanktionen und Haftung – korrekt und ohne Mythen." },
        { titel: "Die zehn Maßnahmen – 40 Min.", text: "Was jeder Maßnahmenbereich im eigenen Betrieb bedeutet und was die Geschäftsführung davon entscheiden muss." },
        { titel: "Planspiel – 30 Min.", text: "„Montag, 7 Uhr, Lösegeldforderung“: Wer entscheidet was, wer meldet binnen 24 Stunden an wen." },
        { titel: "Steuerung – 20 Min.", text: "Welche Kennzahlen und Berichte die Geschäftsführung braucht, welches Budget, welche nächsten Schritte." },
        { titel: "Teilnahmebestätigung", text: "Mit Inhalten und Dauer – als Nachweis, dass die Leitungsorgane teilgenommen haben." },
      ],
    },
    ablauf: {
      heading: "Von der Anfrage bis zum Nachweis",
      schritte: [
        { titel: "Vorgespräch", zeit: "20 Minuten", text: "Wer nimmt teil, wie ist Ihr Betrieb eingestuft, wo sollen die Schwerpunkte liegen." },
        { titel: "Termin", zeit: "meist binnen 2 Wochen", text: "Inhouse in Wien, Niederösterreich und Oberösterreich oder online – wie es für Ihr Team passt." },
        { titel: "Schulung", zeit: "3 Stunden", text: "Fünf Blöcke mit Planspiel, Fragen ausdrücklich erwünscht. Mit Unterlagen zum Nachlesen." },
        { titel: "Nachweis", zeit: "am selben Tag", text: "Teilnahmebestätigung je Person mit Inhalten und Dauer." },
      ],
    },
    preis: {
      betrag: "ab 1.190 €",
      hinweis: "netto zzgl. USt · Fixpreis für bis zu fünf Leitungsorgane · bei Inhouse-Terminen außerhalb Wiens zzgl. Anfahrt",
      enthalten: [
        "Vorgespräch zur Abstimmung der Schwerpunkte",
        "3 Stunden Schulung inhouse oder online",
        "Planspiel mit Ihrem konkreten Betrieb",
        "Unterlagen zum Nachlesen",
        "Teilnahmebestätigung je Person",
      ],
      nichtEnthalten: ["Rechtsberatung im Einzelfall", "Umsetzung der Maßnahmen im Betrieb"],
    },
    vertiefung: [
      {
        heading: "Was § 31 NISG 2026 von der Geschäftsführung verlangt",
        body:
          "§ 31 bündelt die Pflichten der Leitungsorgane: Sie haben die Einhaltung der Risikomanagementmaßnahmen nach § 32 sicherzustellen und zu beaufsichtigen und müssen an speziell für sie gestalteten Cybersicherheitsschulungen teilnehmen. Den Mitarbeitenden hat das Unternehmen regelmäßig entsprechende Schulungen anzubieten – hier geht Österreich über die EU-Richtlinie hinaus, die das nur empfiehlt. Nicht mehr erfasst sind Aufsichtsorgane wie der Aufsichtsrat; eine Informationssicherheitsbeauftragte oder ein CISO fällt nur darunter, soweit tatsächlich Leitungsbefugnisse übertragen wurden.",
      },
      {
        heading: "Was diese Schulung bewusst nicht ist",
        body:
          "Kein Zertifikatslehrgang mit Prüfung und keine Angstveranstaltung. Wir erfinden keine Schulungsfristen und dramatisieren keine Haftung. Einen festen Stundenumfang nennt der Gesetzestext nach unserem Stand nicht – prüfen Sie, ob eine Verordnung oder die Behörde das noch konkretisiert. Was zählt, ist, dass die Geschäftsführung ihre Rolle versteht und Entscheidungen treffen kann.",
      },
      {
        heading: "Warum die Teilnahmebestätigung zählt",
        body:
          "Die Pflicht ist die Teilnahme. Wer sie später belegen muss – gegenüber der Behörde, einem Kunden oder einer Versicherung –, braucht einen Nachweis mit Datum, Dauer und Inhalten. Genau das enthält die Bestätigung. Sie ist zugleich ein Baustein der Selbstdeklaration, die bis 30. September 2027 fällig ist.",
      },
    ],
    faq: [
      { q: "Wer muss an der Schulung teilnehmen?", a: "Die Leitungsorgane betroffener Einrichtungen – in der Praxis Geschäftsführung bzw. Vorstand. Aufsichtsorgane sind nach dem NISG 2026 nicht mehr erfasst. Bei Einrichtungen, die nicht selbst betroffen sind, ist die Schulung freiwillig, aber für Kundenanfragen oft hilfreich." },
      { q: "Gibt es eine Frist, bis wann geschult sein muss?", a: "Die Pflichten gelten ab dem Inkrafttreten am 1. Oktober 2026. Eine eigene Schulungsfrist oder einen festen Stundenumfang haben wir im Gesetz nicht gefunden – wer Ihnen ein Datum als Pflichttermin verkauft, sollte die Quelle nennen können." },
      { q: "Geht das auch online?", a: "Ja. Online oder inhouse, mit identischen Inhalten. Das Planspiel funktioniert in beiden Formaten." },
      { q: "Können wir auch einzeln teilnehmen?", a: "Der Fixpreis gilt für bis zu fünf Leitungsorgane eines Unternehmens. Bei einer einzelnen Person bleibt der Preis gleich, weil Vorbereitung und Planspiel auf Ihren Betrieb zugeschnitten sind." },
      { q: "Ersetzt die Schulung die Umsetzung der Maßnahmen?", a: "Nein. Sie versetzt die Geschäftsführung in die Lage, die Umsetzung der Maßnahmen sicherzustellen und zu beaufsichtigen. Wo Sie stehen und was fehlt, klärt eine Gap-Analyse." },
      { q: "Gibt es Förderungen?", a: foerderhinweis },
    ],
    anlass: "Interesse an der NIS2-Schulung für die Geschäftsführung (§ 31).",
  },
  {
    slug: "nis2-gap-analyse",
    icon: "chart",
    navTitle: "NIS2-Gap-Analyse",
    tagline: "Wo Sie stehen, was als Nächstes kommt – zum Fixpreis.",
    keywords: [
      "nis2 gap analyse",
      "nis2 umsetzung",
      "nis2 beratung",
      "nisg 2026 beratung",
      "nis2 maßnahmen checkliste",
      "§ 32 nisg",
    ],
    metaTitle: "NIS2 Gap-Analyse zum Fixpreis – NISG 2026",
    metaDescription:
      "Ab 1. Oktober gilt das NISG 2026. Die Gap-Analyse zeigt, wo Sie bei den zehn Maßnahmen stehen, mit priorisiertem Plan und Fristenkalender. Ab 4.900 €.",
    heroEyebrow: "Für wesentliche & wichtige Einrichtungen",
    heroHeadline: "Ab 1. Oktober gilt das NISG 2026. Wir zeigen Ihnen, wo Sie stehen – und was als Nächstes kommt.",
    heroSub:
      "Für Unternehmen mit 50–250 Mitarbeitenden, die direkt betroffen sind: Reifegrad je Maßnahme, die größten Risiken, ein priorisierter Plan und Ihre Fristen. Zum Fixpreis.",
    problem: {
      heading: "Wo die meisten Unternehmen gerade stehen",
      punkte: [
        "Sie wissen seit dem WKO-Newsletter, dass Sie betroffen sind – aber nicht, wo Sie anfangen sollen.",
        "Es gibt ein paar Richtlinien von früher, aber niemand weiß, ob sie zum Gesetz passen.",
        "Die Geschäftsführung will einen realistischen Plan mit Kosten, keine 200-seitige Normensammlung.",
      ],
    },
    loesung: {
      heading: "Was die Gap-Analyse liefert",
      intro: "Ein Bericht, den die Geschäftsführung in einer Stunde versteht – und ein Plan, nach dem die IT arbeiten kann.",
      bausteine: [
        { titel: "Einstufung mit Begründung", text: "Wesentlich oder wichtig, mit Sektor, Größe und Konzernzurechnung nachvollziehbar hergeleitet." },
        { titel: "Reifegrad je Maßnahme", text: "Alle zehn Bereiche nach § 32 bewertet, als Grafik auf einen Blick." },
        { titel: "Die größten Risiken", text: "Nicht alles auf einmal: die Lücken, die zuerst geschlossen werden müssen, und warum." },
        { titel: "Priorisierter Maßnahmenplan", text: "Maßnahme, Priorität, Verantwortung, Termin und Aufwand – so konkret, dass die Umsetzung starten kann." },
        { titel: "Ihr Fristenkalender", text: "Registrierung, Selbstdeklaration und interne Meilensteine in einem Überblick." },
        { titel: "Präsentation vor der Geschäftsführung", text: "45–60 Minuten Ergebnisbesprechung, nicht nur ein verschickter Bericht." },
      ],
    },
    ablauf: {
      heading: "In vier Wochen zum Plan",
      schritte: [
        { titel: "Kick-off", zeit: "Tag 1", text: "Umfang, Ansprechpartner, Zeitplan und die Liste der Unterlagen, die wir brauchen." },
        { titel: "Sichtung & Interviews", zeit: "Woche 1–2", text: "Vorhandene Dokumente, Gespräche mit Geschäftsführung, IT, Dienstleister, Produktion und Einkauf." },
        { titel: "Bewertung", zeit: "Woche 3", text: "Reifegrad je Maßnahme, Risikoregister, technische Stichproben – nur mit Ihrer schriftlichen Freigabe." },
        { titel: "Präsentation", zeit: "Woche 4", text: "Management Summary, Maßnahmenplan und Fristenkalender, besprochen mit der Geschäftsführung." },
      ],
    },
    preis: {
      betrag: "ab 4.900 €",
      hinweis: "netto zzgl. USt · Fixpreis nach dem Erstgespräch, abhängig von Standorten und Systemlandschaft",
      enthalten: [
        "Einstufung nach NISG 2026 mit Begründung",
        "Bewertung aller zehn Maßnahmenbereiche",
        "Risikoregister und priorisierter Maßnahmenplan",
        "Fristenkalender Registrierung und Selbstdeklaration",
        "Präsentation vor der Geschäftsführung",
      ],
      nichtEnthalten: [
        "Umsetzung der Maßnahmen (als Begleitung buchbar)",
        "Rechtsberatung",
        "Prüfbericht einer unabhängigen Stelle",
      ],
    },
    vertiefung: [
      {
        heading: "Die zehn Maßnahmenbereiche nach § 32",
        body:
          "§ 32 NISG 2026 verlangt geeignete und verhältnismäßige technische, operative und organisatorische Maßnahmen nach dem Stand der Technik. Die zehn Bereiche reichen von Risikoanalyse und Vorfallbewältigung über Backup und Krisenmanagement, Lieferkette, sichere Beschaffung und Entwicklung bis zu Schulungen, Verschlüsselung, Zugriffskontrolle und Mehr-Faktor-Anmeldung. Die Gap-Analyse bewertet jeden Bereich einzeln – verhältnismäßig heißt dabei: angemessen für Ihren Betrieb, nicht für einen Konzern.",
      },
      {
        heading: "Registrierung und Selbstdeklaration",
        body:
          "Betroffene Einrichtungen registrieren sich binnen drei Monaten nach Inkrafttreten, also bis 31. Dezember 2026. Bis 30. September 2027 folgt die Selbstdeklaration zur Umsetzung der Maßnahmen. Die Gap-Analyse liefert die Grundlage für beides: Die Einstufung brauchen Sie für die Registrierung, den Umsetzungsstand für die Selbstdeklaration.",
      },
      {
        heading: "Wenn die Behörde Nachweise verlangt",
        body:
          "Die Behörde kann Nachweise über die Umsetzung anfordern – unter anderem in Form eines Prüfberichts einer unabhängigen Stelle, der Wirksamkeit und Mängel bewertet und einen Maßnahmenplan enthält. Die Gap-Analyse ersetzt diesen Prüfbericht nicht, bereitet aber genau darauf vor: Wer weiß, wo die Lücken sind, und sie schließt, geht gelassen in eine Prüfung.",
      },
      {
        heading: "Die unterschätzte Lücke: Produktion und Fernwartung",
        body:
          "In Industriebetrieben liegt die größte Lücke oft nicht in der Büro-IT, sondern in der Produktion: Maschinen mit veralteter Software, Fernwartungszugänge von Herstellern, die niemand mehr im Blick hat, flache Netze zwischen Büro und Halle. Mit einem Hintergrund in Maschinensicherheit sehen wir uns diesen Teil ausdrücklich an – inklusive der Frage, welcher Lieferant wann Zugriff hat.",
      },
    ],
    faq: [
      { q: "Wir haben ISO 27001 oder TISAX – brauchen wir das trotzdem?", a: "Dann starten Sie nicht bei null. Statt einer vollständigen Gap-Analyse reicht oft ein Delta-Check: Was deckt Ihr bestehendes System ab, was verlangt das NISG 2026 zusätzlich – etwa beim Meldeprozess oder bei der Registrierung." },
      { q: "Übernehmen Sie auch die Registrierung?", a: "Ja, als eigener Baustein: Einstufung dokumentieren, Angaben sammeln und die Registrierung begleiten, ab 490 €. Die Frist ist der 31. Dezember 2026." },
      { q: "Wie viel Zeit kostet uns das intern?", a: "Rund anderthalb Tage Interviews verteilt auf mehrere Personen, dazu das Zusammenstellen vorhandener Unterlagen. Fehlende Unterlagen sind dabei kein Problem, sondern ein Befund." },
      { q: "Was kostet die Umsetzung danach?", a: "Das hängt vom Ergebnis ab – deshalb enthält der Maßnahmenplan zu jeder Maßnahme eine Aufwandsschätzung. Die Umsetzung können Sie selbst, mit Ihrem IT-Dienstleister oder mit uns angehen." },
      { q: "Machen Sie technische Prüfungen oder Scans?", a: "Nur mit schriftlicher Freigabe, festgelegtem Umfang und Zeitfenster. Ohne Freigabe arbeiten wir ausschließlich mit Unterlagen, Interviews und öffentlich zugänglichen Informationen." },
      { q: "Gibt es Förderungen?", a: foerderhinweis },
    ],
    anlass: "Direkt betroffen (wesentliche/wichtige Einrichtung) – Interesse an einer Gap-Analyse.",
  },
];

export function getLanding(slug: string) {
  return landings.find((l) => l.slug === slug);
}

/** FAQ des Hubs – die Fragen vor der Frage nach einem Paket. */
export const hubFaq = [
  { q: "Was ist der Unterschied zwischen NIS2 und dem NISG 2026?", a: "NIS2 ist die EU-Richtlinie zur Cybersicherheit, das NISG 2026 das österreichische Gesetz, das sie umsetzt. Es gilt ab 1. Oktober 2026 und legt fest, wer betroffen ist und was diese Unternehmen tun müssen." },
  { q: "Woran erkenne ich, ob mein Unternehmen betroffen ist?", a: "Entscheidend sind Branche und Größe: Direkt erfasst sind mittlere und große Unternehmen in den Sektoren der Anlagen 1 und 2 – etwa Energie, Gesundheit, Verkehr, Maschinenbau oder Fahrzeugbau –, einige Dienste auch unabhängig von der Größe. Der kostenlose Betroffenheitscheck gibt in drei Minuten eine erste Einschätzung." },
  { q: "Wir sind zu klein – betrifft uns das trotzdem?", a: "Rechtlich möglicherweise nicht. Wenn Sie aber Unternehmen beliefern, die betroffen sind, verlangen diese Nachweise zur Sicherheit ihrer Lieferkette. Dann entscheidet nicht das Gesetz, sondern ob Sie den Fragebogen Ihres Kunden bestehen." },
  { q: "Gibt es ein NIS2-Zertifikat?", a: "Nein, ein staatliches NIS2-Zertifikat gibt es nicht. Es gibt Nachweise – Dokumente, Protokolle, Prüfberichte. Eine ISO-27001-Zertifizierung hilft, ersetzt die Pflichten aber nicht automatisch." },
  { q: "Was kostet NIS2-Beratung?", a: "Die Pakete haben Fixpreise ab 490 €, das Lieferanten-Paket beginnt bei 2.900 €, die Gap-Analyse bei 4.900 € – jeweils netto. Den genauen Preis bekommen Sie nach dem kostenlosen Erstgespräch." },
  { q: "Ist das Rechtsberatung?", a: "Nein. Wir setzen technisch und organisatorisch um und dokumentieren so, dass es prüfbar ist. Wo eine rechtliche Einzelfallprüfung nötig ist – etwa bei komplexen Konzernstrukturen –, sagen wir das und empfehlen eine Kanzlei." },
  { q: "Wo sind Sie tätig?", a: "Vor Ort in Wien, Niederösterreich und Oberösterreich, remote in ganz Österreich. Schulungen und Quick-Checks funktionieren vollständig online." },
  { q: "Gibt es Förderungen?", a: foerderhinweis },
];
